import { type FC, useEffect, useRef } from 'react'

import { ProductCarouselGalleryImageItem } from './ProductCarouselGalleryImageItem'
import { type ProductCarouselGalleryProps } from './ProductCarouselGalleryProps'
import { ProductCarouselGalleryVideoItem } from './ProductCarouselGalleryVideoItem'

const ProductCarouselGallery: FC<ProductCarouselGalleryProps> = ({
  currentImageIndex,
  images,
  onImageScrolledInView,
  onItemClick,
  playVideoOnClick,
  playVideoOnScrolledIntoView,
  productName,
  useLargeImageSizes,
  videos,
  zoomEnabled = false,
}) => {
  const numberOfMediaObjects = images.length + videos.length
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      const pixelsPerImage =
        containerRef.current.scrollWidth / numberOfMediaObjects

      const nextScrollLeftPosition = currentImageIndex * pixelsPerImage

      containerRef.current.scrollBy({
        behavior: 'smooth',
        left: nextScrollLeftPosition - containerRef.current.scrollLeft,
      })
    }
  }, [currentImageIndex, numberOfMediaObjects])

  const handleScrolledIntoView = (index: number): void => {
    onImageScrolledInView(index)
  }

  return (
    <div
      className="absolute top-0 flex size-full snap-x snap-mandatory flex-nowrap overflow-auto rounded-lg border border-transparent scrollbar-hide hover:border-dark-primary-min"
      ref={containerRef}
    >
      {images.map((image, index) => {
        return (
          <ProductCarouselGalleryImageItem
            image={image}
            index={index}
            key={image}
            lazy={index > 0}
            onClick={(): void => {
              if (onItemClick) {
                onItemClick(index)
              }
            }}
            onScrolledInView={(): void => {
              handleScrolledIntoView(index)
            }}
            productName={productName}
            useLargeSizes={useLargeImageSizes}
            zoomEnabled={zoomEnabled}
          />
        )
      })}
      {videos.map((video, index) => {
        return (
          <ProductCarouselGalleryVideoItem
            index={index}
            key={video.code}
            lazy={images.length + index > 0}
            onClick={(): void => {
              if (onItemClick) {
                onItemClick(images.length + index)
              }
            }}
            onScrolledInView={(): void => {
              handleScrolledIntoView(images.length + index)
            }}
            playOnClick={playVideoOnClick}
            playOnScrolledIntoView={playVideoOnScrolledIntoView}
            productName={productName}
            useLargeSizes={useLargeImageSizes}
            video={video}
            zoomEnabled={zoomEnabled}
          />
        )
      })}
    </div>
  )
}

export { ProductCarouselGallery }
